/**
 * Runs the realtime voice engine while omnibox voice mode is on and the
 * server advertises `voiceEngine: 'realtime'`. Renders nothing — audio and
 * the /voice WS live in the service; captions and the waveform flag flow
 * through the store.
 */
import { forwardRef, useEffect, useRef, useImperativeHandle } from 'react';
import type { WebsiteContext } from '../../../../../../shared/website-context.ts';
import { observer } from 'mobx-react-lite';
import { useMessagingStore, useServices } from '@/app/lib/hooks';
import {
  createVoiceRealtimeSession,
  type VoiceRealtimeSession as VoiceRealtimeSessionHandle,
} from '@/app/lib/services/voice-realtime.service';
import type { BrowserVoiceScreenSelection } from '../../../../../../shared/ws-protocol.ts';
import { useIntl } from 'react-intl';
import { messages } from '@/app/lib/localization/messages.ts';
import { VoiceCaptionDisplay } from './voice-caption-display.ts';

interface RealtimeVoiceSessionProps {
  screenSelection: BrowserVoiceScreenSelection;
  websiteContext?: WebsiteContext;
}

export interface RealtimeVoiceControls {
  stop: () => Promise<void>;
}

const RealtimeVoiceSessionComponent = forwardRef<RealtimeVoiceControls, RealtimeVoiceSessionProps>(
  function RealtimeVoiceSessionComponent({ screenSelection, websiteContext }, ref) {
    const messagesStore = useMessagingStore();
    const { getAttachmentLocale } = useServices();
    const intl = useIntl();
    const intlRef = useRef(intl);
    intlRef.current = intl;
    const active = messagesStore.speechEnabled && messagesStore.voiceEngine === 'realtime';
    const sessionRef = useRef<VoiceRealtimeSessionHandle | null>(null);
    useImperativeHandle(
      ref,
      () => ({
        stop: async () => {
          await sessionRef.current?.stop();
        },
      }),
      [],
    );
    const websiteContextRef = useRef(websiteContext);
    websiteContextRef.current = websiteContext;
    const captionDisplayRef = useRef<VoiceCaptionDisplay | null>(null);
    const screenSelectionRef = useRef(screenSelection);
    screenSelectionRef.current = screenSelection;

    useEffect(() => {
      if (!active) {
        return;
      }
      const captions = new VoiceCaptionDisplay((text) => messagesStore.setVoiceCaption(text));
      captionDisplayRef.current = captions;
      captions.setWorking(messagesStore.voiceRunBusy || messagesStore.userRequestPending);
      const session = createVoiceRealtimeSession({
        getAttachmentLocale,
        getWebsiteContext: () => websiteContextRef.current,
        screenSelection: screenSelectionRef.current,
        getMemories: () => messagesStore.memoryStore.getAll(),
        onAssistantCaption: (delta, startsNewCaption) => {
          captions.append(delta, startsNewCaption);
        },
        onState: (state) => {
          captions.setSpeaking(state === 'speaking');
          if (state === 'connecting' || state === 'thinking') {
            captions.clear();
          }
          messagesStore.setVoiceState(state);
        },
        onRunBusy: (busy, acceptedResponseId) => {
          // A finished run supersedes what was said when it started: without
          // this, the pre-run admission line ("Okay, I'll show…") resurfaces in
          // the status slot between the run ending and the result being spoken.
          if (!busy) {
            captions.clear();
          }
          messagesStore.setVoiceRunBusy(busy, acceptedResponseId);
          captions.setWorking(busy || messagesStore.userRequestPending);
        },
        onMemory: (update) => {
          messagesStore.memoryStore.apply(update);
          return messagesStore.memoryStore.getAll();
        },
        onError: (message) => {
          console.error('[RealtimeVoiceSession]', message);
          const reason = message || intlRef.current.formatMessage(messages.voiceUnknownError);
          messagesStore.notificationsStore.addNotification({
            message: intlRef.current.formatMessage(messages.voiceConnectionProblem, { reason }),
            type: 'error',
            source: 'voice',
          });
        },
        onClose: () => {
          if (sessionRef.current === session && messagesStore.speechEnabled) {
            messagesStore.toggleSpeech();
          }
        },
      });
      sessionRef.current = session;
      messagesStore.toggleRecordingAudio(true);
      session.setMuted(messagesStore.voiceMuted);
      session.start().catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        console.error('[RealtimeVoiceSession] failed to start:', error);
        messagesStore.notificationsStore.addNotification({
          message: intlRef.current.formatMessage(messages.voiceStartFailed),
          type: 'error',
          source: 'voice',
        });
        if (sessionRef.current === session && messagesStore.speechEnabled) {
          messagesStore.toggleSpeech();
        }
      });

      return () => {
        sessionRef.current = null;
        captionDisplayRef.current = null;
        captions.dispose();
        messagesStore.toggleRecordingAudio(false);
        messagesStore.setVoiceCaption('');
        void session.stop();
      };
    }, [active, messagesStore, getAttachmentLocale]);

    const captionWorkPending = messagesStore.voiceRunBusy || messagesStore.userRequestPending;
    useEffect(() => {
      captionDisplayRef.current?.setWorking(captionWorkPending);
    }, [captionWorkPending]);

    const muted = messagesStore.voiceMuted;
    useEffect(() => {
      if (websiteContext !== undefined) sessionRef.current?.updateWebsiteContext(websiteContext);
    }, [websiteContext]);
    useEffect(() => {
      sessionRef.current?.setMuted(muted);
    }, [muted]);

    useEffect(() => {
      sessionRef.current?.updateScreen(screenSelection);
    }, [screenSelection]);

    return null;
  },
);

export const RealtimeVoiceSession = observer(RealtimeVoiceSessionComponent);
